import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Box,
  Typography,
  Toolbar,
  Divider,
  Button,
  Link,
} from '@mui/material';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { deleteInspection, getInspections } from '@/actions/inspection';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import InspectionDeleteDialog from '../InspectionDeleteDialog';
import type { Inspection } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';

const PAGE_SIZE = 20;

interface InspectionWithStatus extends Inspection {
  status?: string;
}

interface InspectionsProps {
  type: INSPECTION_TYPES;
}

function Inspections({ type }: InspectionsProps): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<InspectionWithStatus[] | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [api_error, setApiError] = useState<string | null>(null);

  const base_path =
    type === INSPECTION_TYPES.EFFECTIVE
      ? '/quartermaster/internalAudit'
      : '/quartermaster/evaluator';

  const fetchInspections = useCallback(
    async (page: number, startKey: string | null = null) => {
      if (!api) return;
      setLoading(true);
      try {
        const response = (await getInspections(
          api,
          type,
          PAGE_SIZE,
          startKey || undefined,
        )) as unknown as {
          results: InspectionWithStatus[];
          last_key: string | null;
        };
        setInspections(response.results);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: response.last_key!,
          }));
        }
      } catch (error) {
        console.error('Error fetching inspections:', error);
        setApiError(
          (error as Error).message ||
            'Unable to fetch inspections. This could be due to network issues or server problems.',
        );
        setInspections([]);
      } finally {
        setLoading(false);
      }
    },
    [api, type],
  );

  useEffect(() => {
    if (user?.entity_id && !inspections) {
      setCurrentPage(0);
      setCursors({});
      fetchInspections(0);
    }
  }, [user?.entity_id, inspections, fetchInspections]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page > currentPage && hasNextPage) {
        setCurrentPage(model.page);
        const nextCursor = cursors[model.page];
        if (nextCursor) {
          fetchInspections(model.page, nextCursor);
        }
      } else if (model.page < currentPage) {
        setCurrentPage(model.page);
        if (model.page === 0) {
          fetchInspections(0);
        } else {
          const prevCursor = cursors[model.page];
          if (prevCursor) {
            fetchInspections(model.page, prevCursor);
          }
        }
      }
    },
    [currentPage, hasNextPage, cursors, fetchInspections],
  );

  const handleDeleteInspection = async (
    inspection_id: EntityId,
  ): Promise<void> => {
    if (!api) return;
    try {
      await deleteInspection(api, inspection_id);
      setCurrentPage(0);
      setCursors({});
      await fetchInspections(0);
    } catch (error) {
      console.error('Error deleting inspection:', error);
      setApiError(
        (error as Error).message ||
          'Unable to delete the inspection. This could be due to network issues, server problems, or insufficient permissions.',
      );
    }
  };

  const handleRowSelection = useCallback((value: GridRowSelectionModel) => {
    setRowSelectionModel(value);
  }, []);

  const renderName = useCallback(
    (params: GridRenderCellParams<InspectionWithStatus>) => {
      return (
        <Link component={RouterLink} to={`${base_path}/${params.id}`}>
          {params.row.name}
        </Link>
      );
    },
    [base_path],
  );

  const convertDate = useCallback((value: string | null | undefined) => {
    return value ? new Date(value.replace(' ', 'T') + 'Z') : null;
  }, []);

  const columns = useMemo<GridColDef<InspectionWithStatus>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        flex: 0.5,
      },
      {
        field: 'created_at',
        headerName: 'Started',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (_value: unknown, row: InspectionWithStatus) =>
          convertDate(row.created_at),
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (_value: unknown, row: InspectionWithStatus) =>
          convertDate(row.updated_at),
      },
    ],
    [renderName, convertDate],
  );

  if (!inspections) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          {type === INSPECTION_TYPES.EFFECTIVE
            ? 'Internal Audits'
            : 'Document Evaluations'}
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate(`${base_path}/edit`);
            }}
            variant='contained'
            startIcon={<AddCircleOutlineOutlinedIcon />}
          >
            New{' '}
            {type === INSPECTION_TYPES.EFFECTIVE ? 'Inspection' : 'Evaluation'}
          </Button>
          <Button
            onClick={() => {
              const selectedIds = Array.from(rowSelectionModel.ids);
              navigate({
                pathname: `${base_path}/edit`,
                search: `?inspection_id=${selectedIds[0]}`,
              });
            }}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
              checkboxSelection
              columnHeaderHeight={35}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              getRowId={(row) => row.entity_id}
              columns={columns}
              rows={inspections || []}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={rowSelectionModel}
              showToolbar
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  sx: {
                    button: {
                      color: 'black',
                    },
                    svg: {
                      color: 'black',
                    },
                  },
                  showQuickFilter: true,
                },
              }}
              paginationMode='server'
              rowCount={
                hasNextPage ? -1 : currentPage * PAGE_SIZE + inspections.length
              }
              pageSizeOptions={[PAGE_SIZE]}
              paginationModel={{
                pageSize: PAGE_SIZE,
                page: currentPage,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
            />
      </Box>
      <InspectionDeleteDialog
        key={`delete_${
          inspections?.find((inspection) => {
            const selectedIds = Array.from(rowSelectionModel.ids);
            return inspection.entity_id === selectedIds[0];
          })?.updated_at || ''
        }`}
        inspection={inspections?.find((inspection) => {
          const selectedIds = Array.from(rowSelectionModel.ids);
          return inspection.entity_id === selectedIds[0];
        })}
        open={deleteOpen}
        deleteInspection={handleDeleteInspection}
        handleClose={() => setDeleteOpen(false)}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default Inspections;
